// 소품·메뉴 목록과 가격. 화면과 방 서버가 같은 값을 쓴다.
// id 규칙: 영어 소문자, 한 번 정하면 바꾸지 않는다 (DB 보유·구매 기록과 분석 이벤트에 그대로 저장됨).
// 화면 이름·설명은 여기 두지 않는다 (apps/web/src/text/ 에서 id로 찾는다, 다국어 대비).
//
// 가격: usdRef는 프로토타입 달러 값(참고용), leaf가 실제 단위.
// Leaf 환율은 초안($0.99 = 100 Leaf), Leaf 결제 단계에서 확정. (docs/decisions.md)
import type { RoomId } from './rooms';

export type Slot = 'head' | 'face' | 'hand' | 'size' | 'fur';

export interface CatalogItem {
  slot: Slot;
  /** 이 방에서만 착용 가능. 없으면 모든 방. */
  room?: RoomId;
  usdRef: number;
  leaf: number;
}

/** 초안 환율로 달러 참고값을 Leaf로. 초안, Leaf 결제 단계에서 확정. */
const LEAF_DRAFT: Record<number, number> = { 0: 0, 0.99: 100, 1.29: 150, 1.49: 150, 1.99: 200, 2.99: 300 };
const price = (usdRef: number) => ({ usdRef, leaf: LEAF_DRAFT[usdRef]! });

/** 착용 소품. 'none'은 목록에 넣지 않는다 (빈 칸). */
export const ITEMS = {
  // 머리
  orange: { slot: 'head', ...price(0) },
  yuzu: { slot: 'head', room: 'quiet', ...price(0.99) },
  duckling: { slot: 'head', ...price(0) },
  frog: { slot: 'head', ...price(0) },
  towel: { slot: 'head', room: 'quiet', ...price(0.99) },
  glacier: { slot: 'head', room: 'lagoon', ...price(1.49) },
  puffin: { slot: 'head', room: 'lagoon', ...price(0.99) },
  minigeyser: { slot: 'head', room: 'lagoon', ...price(1.99) },
  auroracloud: { slot: 'head', room: 'lagoon', ...price(1.49) },
  lopapeysa: { slot: 'head', room: 'lagoon', ...price(1.29) },
  hotdog: { slot: 'head', room: 'lagoon', ...price(0.99) },
  yangmeori: { slot: 'head', room: 'jjim', ...price(1.49) },
  stoneegg: { slot: 'head', room: 'jjim', ...price(0.99) },
  bananamilk: { slot: 'head', room: 'jjim', ...price(0.99) },
  bagaji: { slot: 'head', room: 'jjim', ...price(0.99) },
  // 얼굴
  kbeauty: { slot: 'face', room: 'jjim', ...price(1.49) },
  cucumber: { slot: 'face', room: 'jjim', ...price(0.99) },
  bubble: { slot: 'face', ...price(1.99) },
  // 손
  scrubmitt: { slot: 'hand', room: 'jjim', ...price(1.29) },
  scratcher: { slot: 'hand', room: 'jjim', ...price(0.99) },
  // 크기
  baby: { slot: 'size', ...price(2.99) },
  // 털 색
  classic: { slot: 'fur', ...price(0) },
  honey: { slot: 'fur', ...price(0) },
  cocoa: { slot: 'fur', ...price(0) },
} as const satisfies Record<string, CatalogItem>;
export type ItemId = keyof typeof ITEMS;
export type HeadItemId = { [K in ItemId]: (typeof ITEMS)[K]['slot'] extends 'head' ? K : never }[ItemId];
export type FaceItemId = { [K in ItemId]: (typeof ITEMS)[K]['slot'] extends 'face' ? K : never }[ItemId];
export type HandItemId = { [K in ItemId]: (typeof ITEMS)[K]['slot'] extends 'hand' ? K : never }[ItemId];
export type FurId = { [K in ItemId]: (typeof ITEMS)[K]['slot'] extends 'fur' ? K : never }[ItemId];

export interface MenuItem { usdRef: number; leaf: number }

/** 방마다 고르는 메뉴. 라군 팩은 바에서 바르고 씻으면 지워지는 일회성이라 소품과 따로 둔다. */
export const MENUS = {
  /** 조용한 온천 료칸 식당 (무료). */
  ryokan: {
    egg: price(0), dango: price(0), onigiri: price(0), soba: price(0), milk: price(0),
    smilk: price(0), matcha: price(0), sashimi: price(0), sushi: price(0), bento: price(0),
  },
  /** 찜질방 매점 (무료). */
  jjimshop: {
    begg: price(0), kimbap: price(0), ramyeon: price(0), buldak: price(0),
    chicken: price(0), miyeok: price(0), sikhye: price(0), misu: price(0),
  },
  /** 라바 라군 팩 바. */
  maskbar: { silica: price(0), algae: price(0.99), lavaclay: price(1.49) },
  /** 라바 라군 슬러시 바. 핫초코는 털 색 cocoa와 겹치지 않게 hotcocoa (프로토타입 키는 cocoa). */
  slushbar: { slushie: price(0), berry: price(0.99), mango: price(0.99), skyr: price(1.49), hotcocoa: price(0.99) },
} as const satisfies Record<string, Record<string, MenuItem>>;
export type MenuId = keyof typeof MENUS;
export type DishId = keyof typeof MENUS.ryokan | keyof typeof MENUS.jjimshop | keyof typeof MENUS.slushbar;
export type LagoonMaskId = keyof typeof MENUS.maskbar;
